import { XMarkIcon } from '@heroicons/react/24/outline';
import { Modal, ModalContent, Button, Chip } from '@nextui-org/react';
import React from 'react';

import { TypographyUI } from '@portal/components/common/Typography.ui';

interface ModernModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  statusChip?: {
    text: string;
    color: 'success' | 'warning' | 'danger' | 'default';
  };
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  customSize?: {
    width?: string | number;
    height?: string | number;
    minWidth?: string | number;
    minHeight?: string | number;
    maxWidth?: string | number;
    maxHeight?: string | number;
  };
  showCloseButton?: boolean;
  customHeaderActions?: React.ReactNode; // Custom actions before close button
  onClose?: () => void;
}

export const ModernModal: React.FC<ModernModalProps> = ({
  isOpen,
  onOpenChange,
  title,
  subtitle,
  icon,
  statusChip,
  children,
  footer,
  size = 'md',
  customSize,
  showCloseButton = true,
  customHeaderActions,
  onClose,
}) => {
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    onOpenChange();
  };

  const getChipColorClass = (color: string) => {
    switch (color) {
      case 'success':
        return 'bg-green-600 text-white font-semibold h-7 text-sm';
      case 'warning':
        return 'bg-yellow-600 text-white font-semibold h-7 text-sm';
      case 'danger':
        return 'bg-red-600 text-white font-semibold h-7 text-sm';
      default:
        return 'bg-gray-600 text-white font-semibold h-7 text-sm';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={false}
      backdrop='blur'
      placement='top-center'
      size={customSize ? undefined : size}
      hideCloseButton
    >
      <ModalContent className='flex flex-col' style={customSize ? customSize : undefined}>
        {onClose => (
          <div className='relative h-full'>
            {/* Modern Header */}
            <div className='bg-gradient-to-r from-blue-400 to-blue-500 text-white px-6 py-4 rounded-t-lg flex-shrink-0'>
              <div className='flex justify-between items-center'>
                <div className='flex items-center gap-4'>
                  {icon && (
                    <div className='p-2 bg-white/20 rounded-md flex items-center justify-center self-center'>
                      <div className='h-5 w-5 flex items-center justify-center'>{icon}</div>
                    </div>
                  )}
                  <div className='flex flex-col gap-1'>
                    <TypographyUI variant='h6' className='text-white font-bold'>
                      {title}
                    </TypographyUI>
                    {subtitle &&
                      (typeof subtitle === 'string' ? (
                        <TypographyUI variant='caption' className='text-white/80 font-medium'>
                          {subtitle}
                        </TypographyUI>
                      ) : (
                        <div className='text-white/80 font-medium text-sm'>{subtitle}</div>
                      ))}
                  </div>
                </div>
                <div className='flex items-center gap-4'>
                  {statusChip && (
                    <Chip
                      size='sm'
                      variant='flat'
                      color={statusChip.color}
                      className={getChipColorClass(statusChip.color)}
                    >
                      {statusChip.text}
                    </Chip>
                  )}
                  {customHeaderActions}
                  {showCloseButton && (
                    <Button
                      isIconOnly
                      variant='light'
                      size='sm'
                      onPress={handleClose}
                      className='text-white hover:bg-white/20 h-7 w-7 min-w-7'
                    >
                      <XMarkIcon className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Content Container */}
            <div className='p-6 flex-1'>{children}</div>

            {/* Footer - Proportionally sized to match header */}
            <div className='bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 rounded-b-lg flex-shrink-0 border-t border-gray-200'>
              <div className='flex justify-end items-center gap-3'>{footer}</div>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};
